import { createApi } from '@reduxjs/toolkit/query/react';
import { authRequest } from '../store/authorizedRequests';
import { DOWNLOAD_PDF_API_URL } from '../settings/config';
import {
    FetchAiContentAnalysisPdfParams,
    FetchDomainOverviewPdfParams,
    FetchLlmVisibilityPdfParams,
    FetchVisualSeoAnalysisPdfParams,
    PdfReport,
} from './types';

// Every answer is a file rendered from the data the report holds at that moment, so none of it is
// worth keeping: after a fresh analysis a second click has to render the report again instead of
// handing back the file the first click produced.
const KEEP_RENDERED_REPORTS_FOR = 0;

export const reportRenderEngineApi = createApi({
    reducerPath: 'api.reportRenderEngine',

    keepUnusedDataFor: KEEP_RENDERED_REPORTS_FOR,

    baseQuery: async (args, api, extraOptions) => {
        const { url, method, body, signal } = args;
        const { dispatch, getState } = api;
        try {
            const data = await authRequest(url, dispatch, getState, {
                method: method,
                body: JSON.stringify(body),
                signal: signal,
            });

            if (typeof data === 'undefined') {
                return { data: null };
            }

            return { data };
        } catch (error) {
            return { error };
        }
    },

    endpoints: (builder) => ({
        fetchDomainOverviewPdf: builder.query<PdfReport, FetchDomainOverviewPdfParams>({
            query: (args) => {
                const { w_guid } = args;

                return {
                    method: 'GET',
                    url: `${DOWNLOAD_PDF_API_URL}?report_type=domain_overview&w_guid=${encodeURIComponent(w_guid)}`,
                };
            },
        }),

        fetchAiContentAnalysisPdf: builder.query<PdfReport, FetchAiContentAnalysisPdfParams>({
            query: (args) => {
                const { domain } = args;

                return {
                    method: 'GET',
                    url: `${DOWNLOAD_PDF_API_URL}?report_type=ai_content_analysis&domain=${encodeURIComponent(domain)}`,
                };
            },
        }),

        fetchVisualSeoAnalysisPdf: builder.query<PdfReport, FetchVisualSeoAnalysisPdfParams>({
            query: (args) => {
                const { w_guid, url } = args;

                return {
                    method: 'GET',
                    url: `${DOWNLOAD_PDF_API_URL}?report_type=visual_seo_analysis&w_guid=${encodeURIComponent(w_guid)}&url=${encodeURIComponent(url)}`,
                };
            },
        }),

        fetchLlmVisibilityPdf: builder.query<PdfReport, FetchLlmVisibilityPdfParams>({
            query: (args) => {
                const { domain, model_family } = args;

                return {
                    method: 'GET',
                    url: `${DOWNLOAD_PDF_API_URL}?report_type=llm_visibility&domain=${encodeURIComponent(domain)}&model_family=${encodeURIComponent(model_family)}`,
                };
            },
        }),
    }),
});

export const {
    useLazyFetchDomainOverviewPdfQuery,
    useLazyFetchAiContentAnalysisPdfQuery,
    useLazyFetchVisualSeoAnalysisPdfQuery,
    useLazyFetchLlmVisibilityPdfQuery,
} = reportRenderEngineApi;
